/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BulkUpdateFunction, BulkUpdateTarget } from '@gh/builder-shared-data';
import { CanLeaveRoute, getRouteInitData } from '@gh/common-shared';
import { Confirmation, DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { of$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import { Observable } from 'rxjs';
import { BulkUpdateBusinessService } from '../../resources/bulk-update-business.service';
import { BulkUpdateAbstractComponent } from '../bulk-update-abstract.component';

@Component({
  standalone: false,
  selector: 'gh-bulk-update-base',
  templateUrl: 'bulk-update-base.component.html',
  styleUrls: ['bulk-update-container.component.scss'],
})
export class BulkUpdateContainerComponent implements OnInit, CanLeaveRoute {

  @ViewChild('bulkElement', { static: false }) bulkElement: BulkUpdateAbstractComponent;

  bulkUpdateFunction = BulkUpdateFunction;
  bulkUpdateTarget = BulkUpdateTarget;
  functionMode = BulkUpdateFunction.Add;
  targetMode = BulkUpdateTarget.HousePlans;
  functionViewMode = BulkUpdateFunction.Add;
  targetViewMode = BulkUpdateTarget.HousePlans;

  constructor(private route: ActivatedRoute,
              private dialogService: DialogService,
              private businessService: BulkUpdateBusinessService) {
  }

  get isAddFunctionMode(): boolean {
    return this.functionViewMode === BulkUpdateFunction.Add;
  }

  get isReplaceFunctionMode(): boolean {
    return this.functionViewMode === BulkUpdateFunction.Replace;
  }

  get isDeleteFunctionMode(): boolean {
    return this.functionViewMode === BulkUpdateFunction.Delete;
  }

  get isHousePlansTargetMode(): boolean {
    return this.targetViewMode === BulkUpdateTarget.HousePlans;
  }

  get isComponentsTargetMode(): boolean {
    return this.targetViewMode === BulkUpdateTarget.Components;
  }

  get isSaveEnabled(): boolean {
    return !!this.bulkElement && this.bulkElement.dirty;
  }

  ngOnInit(): void {
    assign(this, getRouteInitData(this.route));
  }

  onChangeFunction(mode: BulkUpdateFunction): void {
    this.switchView(mode, this.targetMode);
  }

  onChangeTarget(mode: BulkUpdateTarget): void {
    this.switchView(this.functionMode, mode);
  }

  onSave(): void {
    this.bulkElement.onSave();
  }

  canDeactivate(): Observable<boolean> {
    if (this.bulkElement.dirty) {
      return this.dialogService.confirmation(yesNoConfirmation('msg_changes_will_be_lost')).pipe(
        map$(yesAnswer));
    } else {
      return of$(true);
    }
  }

  protected switchView(fMode: BulkUpdateFunction, tMode: BulkUpdateTarget): void {
    this.functionMode = fMode;
    this.targetMode = tMode;
    if (this.bulkElement.dirty) {
      this.dialogService.confirmation({
        messageId: 'msg_changes_will_be_lost',
        buttons: [Confirmation.Yes, Confirmation.No],
      }).subscribe((confirmation) => {
        switch (confirmation) {
          case Confirmation.Yes:
            this.changeMode(fMode, tMode);
            this.bulkElement.dirty = false;
            break;
          case Confirmation.No:
            this.functionMode = this.functionViewMode;
            this.targetMode = this.targetViewMode;
            break;
          default:
            break;
        }
      });
    } else {
      this.changeMode(fMode, tMode);
    }
  }

  protected changeMode(fMode: BulkUpdateFunction, tMode: BulkUpdateTarget): void {
    this.functionViewMode = fMode;
    this.targetViewMode = tMode;
  }

}
